// A4 文件模板 — ASH Service 風格 (供 PDF 匯出)
import React from 'react';
import { money, fmtDate, CATEGORIES } from './api.js';

const HKD = n => <>HKD$ {money(n)}</>;
const catLabel = c => ({ INSTALLATION: 'MVAC INSTALLATION WORK', MAINTENANCE: 'MAINTENANCE WORK', ECALL: 'EMERGENCY REPAIR (E-CALL)' }[c] || c);

/* ---------- 抬頭 / 頁尾 ---------- */
function AshHead({ settings, title, refNo, refLabel = 'OUR REF. NO.', dateLabel = 'ISSUE DATE', date }) {
  const logoH = Number(settings.company_logo_height) || 160;
  return (
    <>
      <div className="doc-v2-head">
        <div className="doc-v2-brand">
          {settings.company_logo
            ? <img className="doc-v2-logo" style={{ height: logoH + 'px', width: 'auto', maxWidth: Math.round(logoH * 1.3) + 'px' }} src={settings.company_logo} alt="logo" />
            : <div className="doc-v2-logo-badge">{(settings.company_name || 'ASH').slice(0, 3).toUpperCase()}</div>}
          <div style={{ alignSelf: 'center' }}>
            <div className="doc-v2-co">{settings.company_name || 'ASH Service Co. Ltd.'}</div>
            <div className="doc-v2-co-sub">
              {settings.company_address ? settings.company_address + '\n' : ''}
              {settings.company_phone ? `Tel: ${settings.company_phone}` : ''}{settings.company_fax ? `  Fax: ${settings.company_fax}` : ''}
            </div>
          </div>
        </div>
        <div className="doc-v2-title">
          <div className="t">{title}</div>
          <div className="doc-v2-ref">
            <b>{refLabel}:</b> {refNo}<br />
            {date ? <><b>{dateLabel}:</b> {date}<br /></> : null}
          </div>
        </div>
      </div>
      <div className="doc-hr2" />
    </>
  );
}

function AshFoot({ settings, extra }) {
  return (
    <div className="doc-v2-foot">
      <div>{settings.company_email || ''}{extra ? <><br />{extra}</> : null}</div>
      <div className="mid">- The End -</div>
      <div className="slogan">{settings.company_tagline || 'We do a lot more than introducing clean air into a space.'}</div>
    </div>
  );
}

/* ---------- 簽名欄 (我方 = 兩個公司印章) ---------- */
function ChopArea({ settings }) {
  if (!settings.company_chop1 && !settings.company_chop2) {
    return <div className="gap"><span className="doc-v2-chop">Company Chop 印章</span></div>;
  }
  return (
    <div className="chop-area">
      {settings.company_chop1 ? <img className="doc-chop-img r1" src={settings.company_chop1} alt="chop1" /> : null}
      {settings.company_chop2 ? <img className="doc-chop-img r2" src={settings.company_chop2} alt="chop2" /> : null}
    </div>
  );
}

function AshSign({ settings, customerName }) {
  return (
    <div className="doc-v2-sign">
      <div className="col">
        <div><b>Yours Sincerely,</b></div>
        <ChopArea settings={settings} />
        <div className="line">For {settings.company_name || 'ASH Service Co. Ltd.'}<br />Authorised Signature 簽名</div>
      </div>
      <div className="col">
        <div><b>Confirmed and Accepted by:</b> {customerName || ''}</div>
        <div className="gap"></div>
        <div className="line">Company Seal 客戶蓋章<br />Date 日期: <span style={{ letterSpacing: 2 }}>____ / ____ / ________</span></div>
      </div>
    </div>
  );
}

/* ---------- 收款 / 付款時間表 ---------- */
function PaymentSchedule({ total, installments }) {
  if (!installments || !installments.length) return null;
  let cum = 0;
  const rows = installments.map((ins, i, arr) => {
    const pct = Number(ins.percent) || 0;
    const amt = i === arr.length - 1
      ? Math.round((total - cum) * 100) / 100
      : Math.round(total * pct / 100 * 100) / 100;
    cum += amt;
    return { label: ins.label || `INSTALLMENT ${i + 1}`, pct, amt, due: ins.due_days };
  });
  return (
    <div>
      <div className="doc-sec-h">PAYMENT SCHEDULE 付款時間表</div>
      <table className="doc-v2-tbl">
        <thead>
          <tr>
            <th>NO.</th><th>INSTALLMENT 分期</th><th className="c">% OF GRAND TOTAL</th><th className="r">AMOUNT (HKD$)</th><th className="c">DUE 到期</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="c">{i + 1}</td>
              <td>{r.label}</td>
              <td className="c">{r.pct}%</td>
              <td className="r"><b>{money(r.amt)}</b></td>
              <td className="c">{r.due === 0 || r.due ? `Upon + ${r.due} days` : '—'}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={3}><b>PROJECT SUM (GRAND TOTAL)</b></td>
            <td className="r"><b>{money(total)}</b></td>
            <td className="c">—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ============ 報價單 QUOTATION (ASH 正式格式 — 照 Excel/PDF 樣本) ============ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtD = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
};
const fmtMoney = n => {
  const v = Number(n) || 0;
  return '$ ' + (Math.abs(v % 1) < 0.005 ? Math.round(v).toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2 }));
};

/* 行分類: 定價行(自動編號 N/) / 子彈(- 開頭) / 分區標題(冒號結尾) / 連續描述 */
function classifyItems(items) {
  const rows = [];
  let n = 0;
  const sections = [];
  let cur = { header: null, first: null, last: null, sum: 0 };
  const closeSection = () => { if (cur.first !== null) sections.push(cur); };
  for (const it of items) {
    const desc = (it.description || '').trim();
    const hasPrice = (Number(it.qty) || 0) > 0 && (Number(it.unit_price) || 0) > 0;
    if (hasPrice) {
      n += 1;
      rows.push({ type: 'item', no: n + '/', desc: desc.replace(/^-\s*/, ''), qty: Number(it.qty), unit: it.unit || '', rate: Number(it.unit_price), amt: Math.round(Number(it.qty) * Number(it.unit_price) * 100) / 100 });
      if (cur.first === null) cur.first = n;
      cur.last = n;
      cur.sum += Number(it.qty) * Number(it.unit_price);
    } else if (desc.startsWith('-')) {
      rows.push({ type: 'bullet', desc });
    } else if (/[:-]\s*$/.test(desc)) {
      if (desc.length <= 30) { // 短標題 = 分區 (例如 Hot Pot Area:-) → 出小計
        closeSection();
        cur = { header: desc, first: null, last: null, sum: 0 };
        rows.push({ type: 'header', desc });
      } else { // 長描述 (… c/w:-) = 母項描述 → 只加粗, 唔開新區
        rows.push({ type: 'parent', desc });
      }
    } else if (desc) {
      rows.push({ type: 'plain', desc });
    }
  }
  closeSection();
  const subtotals = sections.filter(s => s.header && s.first !== null)
    .map(s => ({ label: 'TOTAL OF ITEMS ' + s.first + (s.last > s.first ? ' -' + s.last : '') + ' (HKD$)', sum: Math.round(s.sum * 100) / 100 }));
  return { rows, n, subtotals };
}

/* 付款時間表 (由付款條款生成) */
function PaymentScheduleTable({ total, installments }) {
  if (!installments || !installments.length) return null;
  const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth'];
  const cols = installments.map((ins, i) => ({
    phase: String(ins.label || '').replace(/\s*\([^)]*\)\s*$/, '').replace(/\s*\d+%\s*$/, '').trim() || 'Stage ' + (i + 1),
    inst: (ordinals[i] || 'No.' + (i + 1)) + ' Instalment',
    pct: Number(ins.percent) || 0,
    amt: Math.round(total * (Number(ins.percent) || 0) / 100 * 100) / 100,
  }));
  let cum = 0;
  cols.forEach((c, i) => { if (i < cols.length - 1) cum += c.amt; else c.amt = Math.round((total - cum) * 100) / 100; });
  const due = (installments[0] && installments[0].due_days != null) ? installments[0].due_days : 14;
  return (
    <div>
      <div className="doc-sec-h">PAYMENT SCHEDULE</div>
      <table className="doc-v2-tbl doc-ps-tbl">
        <tbody>
          <tr>
            <td className="lbl">Project Phase</td>
            {cols.map((c, i) => <td key={i} className="c b">{c.phase}</td>)}
            <td className="c b">Project Sum<br />(HKD$)</td>
          </tr>
          <tr>
            <td className="lbl">Instalment</td>
            {cols.map((c, i) => <td key={i} className="c">{c.inst}</td>)}
            <td></td>
          </tr>
          <tr>
            <td className="lbl">% of Grand Total</td>
            {cols.map((c, i) => <td key={i} className="c">{c.pct}%</td>)}
            <td></td>
          </tr>
          <tr>
            <td className="lbl">Amount (HKD$)</td>
            {cols.map((c, i) => <td key={i} className="r b">{Math.round(c.amt).toLocaleString('en-US')}</td>)}
            <td className="r b">{Math.round(total).toLocaleString('en-US')}</td>
          </tr>
          <tr>
            <td className="lbl">Remark</td>
            <td colSpan={cols.length + 1} className="remark">Each instalment payment is due {due} days from the individual invoice date.</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function QuotationDoc({ data, settings }) {
  const { rows, n, subtotals } = classifyItems(data.items || []);
  const itemsSum = Math.round((data.subtotal != null ? Number(data.subtotal) : rows.filter(r => r.type === 'item').reduce((s, r) => s + r.amt, 0)) * 100) / 100;
  const grand = (data.total != null ? Number(data.total) : itemsSum);
  const disc = Math.round((itemsSum - grand) * 100) / 100;
  const phone = (settings.company_phone || '').trim();
  const st = settings;
  const logoH = Number(st.company_logo_height) || 160;
  const workType = ({ INSTALLATION: 'MVAC Installation Work', MAINTENANCE: 'MVAC Maintenance Work', ECALL: 'Emergency Repair (E-Call)' })[data.category] || data.category;

  return (
    <div className="doc-page">
      <div className="doc-v2-head">
        <div className="doc-v2-brand">
          {st.company_logo
            ? <img className="doc-v2-logo" style={{ height: logoH + 'px', width: 'auto', maxWidth: Math.round(logoH * 1.3) + 'px' }} src={st.company_logo} alt="logo" />
            : <div className="doc-v2-logo-badge">ASH</div>}
          <div style={{ alignSelf: 'center' }}>
            <div className="doc-v2-co">{st.company_name || 'ASH Service Co. Ltd.'}</div>
            <div className="doc-v2-co-sub">{st.company_email ? 'Email to ' + st.company_email : ''}</div>
          </div>
        </div>
        <div className="doc-v2-title"><div className="t">QUOTATION</div></div>
      </div>
      <div className="doc-hr2" />

      <div className="qo-info-row">
        <div className="doc-v2-box">
          <div className="h">ATTENTION TO</div>
          <div className="l">
            <b>Company:</b> {data.customer_name}{'\n'}
            <b>Address:</b> {data.office_address || '-'}{'\n'}
            <b>Recipient:</b> {data.customer_contact || '-'}
          </div>
        </div>
        <div className="qo-ref">
          <div className="h">OUR REF. NO.</div>
          <div className="no">{data.quote_no}</div>
        </div>
      </div>

      <div className="qo-info-row">
        <div className="doc-v2-box">
          <div className="h">PROJECT CONCERNED</div>
          <div className="l">
            <b>Site:</b> {data.title || '-'}{'\n'}
            <b>Type of Work:</b> {workType}
          </div>
        </div>
        <div className="qo-ref">
          <div className="h">ISSUE DATE</div>
          <div className="no">{fmtD(data.issue_date)}</div>
        </div>
      </div>

      <div className="qo-big-title">QUOTATION</div>

      <table className="doc-v2-tbl qo-tbl">
        <thead>
          <tr>
            <th className="c" style={{ width: 44 }}>ITEM</th>
            <th>DESCRIPTION</th>
            <th className="c" style={{ width: 52 }}>QTY</th>
            <th className="c" style={{ width: 46 }}>UNIT</th>
            <th className="r" style={{ width: 92 }}>UNIT RATE<br />(HKD$)</th>
            <th className="r" style={{ width: 96 }}>AMOUNT<br />(HKD$)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            if (r.type === 'header') return <tr key={i}><td className="c"></td><td className="sect">{r.desc}</td><td></td><td></td><td></td><td></td></tr>;
            if (r.type === 'parent') return <tr key={i}><td className="c"></td><td className="sect">{r.desc}</td><td></td><td></td><td></td><td></td></tr>;
            if (r.type === 'bullet') return <tr key={i}><td className="c"></td><td className="bullet">{r.desc}</td><td></td><td></td><td></td><td></td></tr>;
            if (r.type === 'plain') return <tr key={i}><td className="c"></td><td>{r.desc}</td><td></td><td></td><td></td><td></td></tr>;
            return (
              <tr key={i}>
                <td className="c b">{r.no}</td>
                <td>{r.desc}</td>
                <td className="c">{r.qty}</td>
                <td className="c">{r.unit}</td>
                <td className="r">{fmtMoney(r.rate)}</td>
                <td className="r b">{fmtMoney(r.amt)}</td>
              </tr>
            );
          })}
          {subtotals.map((s, i) => (
            <tr key={'st' + i} className="qo-sub">
              <td colSpan={5} className="r sublbl">{s.label}</td>
              <td className="r b">{fmtMoney(s.sum)}</td>
            </tr>
          ))}
          <tr className="qo-grand">
            <td colSpan={5} className="r">{n ? <>GRAND TOTAL OF ITEMS 1 -{n} (HKD$)</> : <>GRAND TOTAL (HKD$)</>}</td>
            <td className="r">{fmtMoney(itemsSum)}</td>
          </tr>
          {disc > 0 ? (
            <>
              <tr className="qo-sub"><td colSpan={5} className="r sublbl">DISCOUNT {data.discount_type === 'percent' ? '(' + data.discount_value + '%)' : ''}</td><td className="r b">- {fmtMoney(disc)}</td></tr>
              <tr className="qo-grand"><td colSpan={5} className="r">GRAND TOTAL AFTER DISCOUNT (HKD$)</td><td className="r">{fmtMoney(grand)}</td></tr>
            </>
          ) : null}
        </tbody>
      </table>

      {data.remarks && data.remarks.length ? (
        <div>
          <div className="doc-sec-h">IMPORTANT NOTES</div>
          <div className="doc-notes">
            <div className="nc-h">ITEMS NOT COVERED</div>
            {data.remarks.map((r, i) => <div key={i}>{r.startsWith('-') ? r : '- ' + r}</div>)}
          </div>
        </div>
      ) : null}

      {data.payment_installments && data.payment_installments.length ? <PaymentScheduleTable total={grand} installments={data.payment_installments} /> : null}

      <div className="qo-thanks">
        <div>Thank you for your interest in working with us.</div>
        <div>Should you have any inquiries about this quotation, please do not hesitate to contact the undersigned{phone ? ' at ' + phone : ''}.</div>
      </div>

      <div className="doc-v2-sign qo-sign">
        <div className="col">
          <div><b>Yours Sincerely,</b></div>
          <ChopArea settings={settings} />
          <div className="sign-name">{st.company_signatory || ''}</div>
        </div>
        <div className="col">
          <div><b>Confirmed and Accepted by</b></div>
          <div className="cust-name">{data.customer_name}</div>
          <div className="gap-s"></div>
          <div className="line">Company Seal</div>
          <div className="line">Date</div>
          <div className="line dd">DD/MM/YYYY</div>
        </div>
      </div>

      <AshFoot settings={settings} />
    </div>
  );
}

/* ============ 訂單 WORK ORDER ============ */
export function OrderDoc({ data, settings }) {
  return (
    <div className="doc-page">
      <AshHead settings={settings} title="WORK ORDER" refNo={data.order_no} date={fmtDate(data.order_date)} />
      <div className="doc-v2-info">
        <div className="doc-v2-box">
          <div className="h">ATTENTION TO</div>
          <div className="l">
            <b>Company:</b> {data.customer_name}{'\n'}
            <b>Address:</b> {data.office_address || '-'}{'\n'}
            <b>Recipient:</b> {data.customer_contact || '-'}
          </div>
        </div>
        <div className="doc-v2-box">
          <div className="h">PROJECT CONCERNED</div>
          <div className="l">
            <b>Type of Work:</b> {catLabel(data.category)}{'\n'}
            {data.title ? <><b>Description:</b> {data.title}{'\n'}</> : null}
            <b>Completion:</b> {fmtDate(data.completion_date)}{'\n'}
            {data.quote_no ? <><b>From Quotation:</b> {data.quote_no}</> : null}
          </div>
        </div>
      </div>
      <table className="doc-v2-tbl">
        <thead>
          <tr>
            <th className="c" style={{ width: 42 }}>ITEM</th><th>DESCRIPTION</th>
            <th className="c" style={{ width: 58 }}>QTY</th><th className="c" style={{ width: 54 }}>UNIT</th>
            <th className="r" style={{ width: 92 }}>UNIT RATE</th><th className="r" style={{ width: 100 }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {(data.items || []).map((it, i) => (
            <tr key={i}>
              <td className="c">{i + 1}</td><td><b>{it.description}</b></td>
              <td className="c">{it.qty}</td><td className="c">{it.unit}</td>
              <td className="r">{money(it.unit_price)}</td><td className="r"><b>{money(it.amount)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="doc-v2-total">
        <table>
          <tbody>
            <tr><td>SUBTOTAL</td><td className="r">{HKD(data.subtotal)}</td></tr>
            <tr className="grand"><td>CONTRACT SUM (HKD$)</td><td className="r">{money(data.total)}</td></tr>
          </tbody>
        </table>
      </div>
      {data.remarks?.length ? (
        <div>
          <div className="doc-sec-h">TERMS & CONDITIONS 條款</div>
          <div className="doc-notes">{data.remarks.map((r, i) => <div key={i}>• {r}</div>)}</div>
        </div>
      ) : null}
      <AshSign settings={settings} customerName={data.customer_name} />
      <AshFoot settings={settings} />
    </div>
  );
}

/* ============ 發票 INVOICE ============ */
export function InvoiceDoc({ data, settings }) {
  const remain = (data.adjusted_amount ?? data.amount) - (data.paid_amount || 0);
  const lines = (data.order_items && data.order_items.length
    ? data.order_items
    : [{ description: data.order_title || data.installment_label || 'MVAC WORKS', qty: 1, unit: 'LS', unit_price: data.amount, amount: data.amount }]);
  return (
    <div className="doc-page">
      <AshHead settings={settings} title="INVOICE" refNo={data.invoice_no} date={fmtDate(data.issue_date)} />
      <div className="doc-v2-info">
        <div className="doc-v2-box">
          <div className="h">ATTENTION TO</div>
          <div className="l">
            <b>Company:</b> {data.customer_name}{'\n'}
            <b>Address:</b> {data.office_address || '-'}{'\n'}
            <b>Recipient:</b> {data.customer_contact || '-'}
          </div>
        </div>
        <div className="doc-v2-box">
          <div className="h">PROJECT CONCERNED</div>
          <div className="l">
            {data.order_no ? <><b>Work Order:</b> {data.order_no}{'\n'}</> : null}
            {data.order_title ? <><b>Description:</b> {data.order_title}{'\n'}</> : null}
            <b>Installment:</b> {data.installment_label || 'Full Payment'}{data.installment_count > 1 ? ` (${data.installment_no}/${data.installment_count})` : ''}{'\n'}
            <b>Payment Due:</b> {fmtDate(data.due_date)}
          </div>
        </div>
      </div>
      <table className="doc-v2-tbl">
        <thead>
          <tr>
            <th className="c" style={{ width: 42 }}>ITEM</th><th>DESCRIPTION</th>
            <th className="c" style={{ width: 58 }}>QTY</th><th className="c" style={{ width: 54 }}>UNIT</th>
            <th className="r" style={{ width: 92 }}>UNIT RATE</th><th className="r" style={{ width: 100 }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((it, i) => (
            <tr key={i}>
              <td className="c">{i + 1}</td><td><b>{it.description}</b></td>
              <td className="c">{it.qty}</td><td className="c">{it.unit}</td>
              <td className="r">{money(it.unit_price)}</td><td className="r"><b>{money(it.amount)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="doc-v2-total">
        <table>
          <tbody>
            <tr><td>INVOICE AMOUNT</td><td className="r">{HKD(data.amount)}</td></tr>
            {data.adjusted_amount !== data.amount ? <tr><td>ADJUSTED 息爭後應收</td><td className="r">{HKD(data.adjusted_amount)}</td></tr> : null}
            {data.paid_amount > 0 ? <tr><td>RECEIVED 已收</td><td className="r">- {HKD(data.paid_amount)}</td></tr> : null}
            <tr className="grand"><td>AMOUNT DUE 應收 (HKD$)</td><td className="r">{money(remain)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="doc-notes" style={{ marginTop: 10 }}>
        {data.term_installments ? <div>• Each installment payment is due {data.term_installments[data.installment_no - 1]?.due_days ?? 30} days from the individual invoice date.</div> : <div>• Payment is due on or before the payment due date.</div>}
        <div>• Please make payment by cheque payable to <b>{settings.company_name || 'ASH Service Co. Ltd.'}</b> or by bank transfer.</div>
        <div>• Any query, please quote our invoice number.</div>
      </div>
      {(data.payments || []).length ? (
        <div>
          <div className="doc-sec-h">PAYMENT RECORDS 收款紀錄</div>
          <div className="doc-notes">{(data.payments || []).map((p, i) => <div key={i}>• {fmtDate(p.pay_date)} — HKD$ {money(p.amount)} ({p.method || '-'})</div>)}</div>
        </div>
      ) : null}
      {data.status === 'paid' ? <div className="doc-v2-stamp">PAID 已付清</div> : null}
      <AshSign settings={settings} customerName={data.customer_name} />
      <AshFoot settings={settings} />
    </div>
  );
}

/* ============ 對帳單 STATEMENT ============ */
export function StatementDoc({ data, settings }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="doc-page">
      <AshHead settings={settings} title="STATEMENT" refNo={`STMT-${today.replace(/-/g, '')}`} date={fmtDate(today)} />
      <div className="doc-v2-info">
        <div className="doc-v2-box">
          <div className="h">ATTENTION TO</div>
          <div className="l">
            <b>Company:</b> {data.customer.name}{'\n'}
            {data.customer.contact ? <><b>Attn:</b> {data.customer.contact}{'\n'}</> : null}
            {data.customer.phone ? <><b>Tel:</b> {data.customer.phone}</> : null}
          </div>
        </div>
        <div className="doc-v2-box">
          <div className="h">ACCOUNT SUMMARY</div>
          <div className="l">
            <b>Outstanding Invoices 未清發票:</b> {data.invoices.length} 張{'\n'}
            <b>Statement Date 結算日:</b> {fmtDate(today)}
          </div>
        </div>
      </div>
      <table className="doc-v2-tbl">
        <thead>
          <tr>
            <th>INVOICE</th><th>WORK ORDER</th><th>DESCRIPTION</th>
            <th className="c">ISSUE</th><th className="c">DUE</th>
            <th className="r">AMOUNT</th><th className="r">PAID</th><th className="r">BALANCE</th>
          </tr>
        </thead>
        <tbody>
          {data.invoices.map(inv => (
            <tr key={inv.id}>
              <td><b>{inv.invoice_no}</b></td>
              <td>{inv.order_no || '—'}</td>
              <td>{inv.installment_label || inv.order_title || ''}</td>
              <td className="c">{fmtDate(inv.issue_date)}</td>
              <td className="c" style={{ color: inv.due_date && inv.due_date < today ? '#c00000' : undefined, fontWeight: inv.due_date && inv.due_date < today ? 800 : undefined }}>{fmtDate(inv.due_date)}</td>
              <td className="r">{money(inv.adjusted_amount)}</td>
              <td className="r">{money(inv.paid_amount)}</td>
              <td className="r"><b>{money(inv.adjusted_amount - inv.paid_amount)}</b></td>
            </tr>
          ))}
          <tr>
            <td colSpan={7}><b>TOTAL OUTSTANDING 總欠款</b></td>
            <td className="r"><b>{money(data.total_outstanding)}</b></td>
          </tr>
        </tbody>
      </table>
      <div className="doc-sec-h">AGING ANALYSIS 帳齡分析</div>
      <table className="doc-v2-tbl">
        <thead>
          <tr><th className="c">CURRENT 未到期</th><th className="c">1-30 DAYS</th><th className="c">31-60 DAYS</th><th className="c">61-90 DAYS</th><th className="c">OVER 90 DAYS</th></tr>
        </thead>
        <tbody>
          <tr>
            <td className="r"><b>{money(data.aging.current)}</b></td>
            <td className="r"><b>{money(data.aging.d30)}</b></td>
            <td className="r"><b>{money(data.aging.d60)}</b></td>
            <td className="r"><b>{money(data.aging.d90)}</b></td>
            <td className="r"><b>{money(data.aging.over)}</b></td>
          </tr>
        </tbody>
      </table>
      <div className="doc-notes">
        <div>• Settlement of the above outstanding balance is much appreciated.</div>
        <div>• Cheque payable to <b>{settings.company_name}</b>; mail to {settings.company_address || 'our office'}.</div>
        <div>• Any query, please contact {settings.company_phone || 'our accounts department'}.</div>
      </div>
      <AshSign settings={settings} customerName={data.customer.name} />
      <AshFoot settings={settings} />
    </div>
  );
}

/* ============ 採購單 PURCHASE ORDER ============ */
export function PODoc({ data, settings }) {
  const lines = data.items?.length ? data.items : [{ description: data.notes || 'AS PER ATTACHED PO', qty: 1, unit: 'LS', unit_price: data.total, amount: data.total }];
  return (
    <div className="doc-page">
      <AshHead settings={settings} title="PURCHASE ORDER" refNo={data.po_no} date={fmtDate(data.po_date)} />
      <div className="doc-v2-info">
        <div className="doc-v2-box">
          <div className="h">SUPPLIER</div>
          <div className="l">
            <b>Company:</b> {data.supplier_name}{'\n'}
            {data.supplier_contact ? <><b>Attn:</b> {data.supplier_contact}{'\n'}</> : null}
            {data.supplier_phone ? <><b>Tel:</b> {data.supplier_phone}</> : null}
          </div>
        </div>
        <div className="doc-v2-box">
          <div className="h">PROJECT CONCERNED</div>
          <div className="l">
            <b>Work Order:</b> {data.order_no || '—'}{'\n'}
            {data.order_title ? <><b>Description:</b> {data.order_title}{'\n'}</> : null}
            <b>Delivery Date:</b> {fmtDate(data.delivery_date)}
          </div>
        </div>
      </div>
      <table className="doc-v2-tbl">
        <thead>
          <tr>
            <th className="c" style={{ width: 42 }}>ITEM</th><th>DESCRIPTION</th>
            <th className="c" style={{ width: 58 }}>QTY</th><th className="c" style={{ width: 54 }}>UNIT</th>
            <th className="r" style={{ width: 92 }}>UNIT PRICE</th><th className="r" style={{ width: 100 }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((it, i) => (
            <tr key={i}>
              <td className="c">{i + 1}</td><td><b>{it.description}</b></td>
              <td className="c">{it.qty}</td><td className="c">{it.unit}</td>
              <td className="r">{money(it.unit_price)}</td><td className="r"><b>{money(it.amount)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="doc-v2-total">
        <table>
          <tbody>
            <tr className="grand"><td>P.O. TOTAL (HKD$)</td><td className="r">{money(data.total)}</td></tr>
          </tbody>
        </table>
      </div>
      {data.notes ? (
        <div>
          <div className="doc-sec-h">NOTES 備註</div>
          <div className="doc-notes"><div>• {data.notes}</div></div>
        </div>
      ) : null}
      <div className="doc-v2-sign">
        <div className="col">
          <div><b>Approved by:</b></div>
          <ChopArea settings={settings} />
          <div className="line">For {settings.company_name || 'ASH Service Co. Ltd.'}<br />Authorised Signature 簽名</div>
        </div>
        <div className="col">
          <div><b>Supplier Confirmation:</b></div>
          <div className="gap"></div>
          <div className="line">For {data.supplier_name}<br />Company Seal 蓋章</div>
        </div>
      </div>
      <AshFoot settings={settings} />
    </div>
  );
}

/* ---------- 預覽容器 (Modal 內 1:1 顯示) ---------- */
export function DocPreview({ children, onClose, title = '文件預覽', onPDF }) {
  return (
    <div className="modal-mask" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal xl">
        <div className="modal-head">
          <span className="ico-badge">👁️</span>
          <div className="modal-title">{title}</div>
          {onPDF && <button className="btn primary" onClick={onPDF}>📥 出 PDF</button>}
          <button className="btn" onClick={onClose}>✕ 關閉</button>
        </div>
        <div className="modal-body" style={{ background: '#dfe6f0', padding: 16 }}>
          <div style={{ width: 794, margin: '0 auto', boxShadow: '0 10px 30px rgba(0,0,0,.25)' }}>{children}</div>
        </div>
      </div>
    </div>
  );
}
